<template>
  <!-- Task Card Content -->
  <div 
    v-for="(attachment, index) in messageData.attachments"
    :key="index" 
    :class="bubbleContentClass"
    :style="{ marginBottom: index < messageData.attachments.length - 1 ? '8px' : '0' }"
  >
    <div 
      class="task-card-content"
    >
      <!-- Task Header -->
      <div class="task-header">
        <div class="task-icon w-12 h-12 flex items-center justify-center bg-[#f2f2f2]">
          <img src="@/assets/MyPatient/document.svg" alt="Task" />
        </div>
        <div class="task-info">
          <div class="task-title truncate">
            {{ getDocumentName(attachment) }}
          </div>
          <div class="task-meta">
            <span class="task-due-date">Create On: {{ getCreatedDate(attachment) }}</span>
            <span class="task-status">{{ getFileTypeAndSize(attachment) }}</span>
          </div>
        </div>
      </div>

      <!-- Task Description -->
      <div class="task-description"></div>

      <!-- Task Actions -->
      <div class="task-actions">
        <button class="send-again-btn" @click="previewDocument(attachment)">
          <img
            src="@/assets/MyPatient/preview.svg"
            alt="Preview"
            class="w-[18px] h-[18px]"
          />
          <span>Preview</span>
        </button>
        <!-- Only show Download button for uploaded files and reports, not for returned forms -->
        <button 
          v-if="!isReturnedForm(attachment)"
          class="send-again-btn" 
          @click="downloadDocument(attachment)"
        >
          <img
            src="@/assets/MyPatient/file_download.svg"
            alt="Download"
            class="w-[18px] h-[18px]"
          />
          <span>Download</span>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { downloadPatientFileApi } from '@/api/patientFiles'
import axios from 'axios';
import { notify } from '@/utils/notify';

export default {
  name: "MessageCardTask",
  props: {
    messageData: {
      type: Object,
      required: true,
    },
    isSent: {
      type: Boolean,
      default: false,
    },
    rc_user_id: {
      type: String,
      required: true,
    },
    patientInfo: {
      type: Object,
      required: true,
    }
  },
  data() {
    return {
      accessionId: null,
      fileid: 'cmfbd80w70000hb0173giorc6',
      fileUrl: null,
      cardFileUrl: null, // Store the generated URL from downloadUploadedFile()
      isDownloading: false, // Loading indicator for file download/generation
    };
  },
  computed: {
    isCurrentUser() {
      return this.messageData.u._id === this.rc_user_id;
    },
    
    isBotMessage() {
      return this.messageData.u._id === 'oafKicny6TymdzNxQ' || 
             this.messageData.u._id === 'EuMKAsjDZTsLc74Kw';
    },
    
    bubbleContentClass() {
      if (this.isBotMessage) {
        return 'bubble-content not-user-bubble task-card-bubble';
      }
      return this.isCurrentUser 
        ? 'bubble-content task-card-bubble' 
        : 'bubble-content not-user-bubble task-card-bubble';
    }
  },
  mounted() {
    console.log(this.messageData);
    // File URL will be generated only when download/preview is clicked
  },
  methods: {
    /**
     * Get field value from attachment by field title
     */
    getFieldValue(attachment, fieldTitle) {
      if (!attachment?.fields) {
        return '';
      }
      const field = attachment.fields.find(f => f.title === fieldTitle);
      return field ? field.value : '';
    },
    
    /**
     * Get document name from fields
     */
    getDocumentName(attachment) {
      return this.getFieldValue(attachment, 'name') || 'Unknown Document';
    },
    
    /**
     * Get document creation date formatted as MM-DD-YYYY
     */
    getCreatedDate(attachment) {
      const created = this.getFieldValue(attachment, 'created');
      if (!created) return '01-01-2025';
      
      try {
        const date = new Date(created);
        // Format as MM-DD-YYYY with dashes
        return date.toLocaleDateString('en-US', {
          month: '2-digit',
          day: '2-digit', 
          year: 'numeric'
        }).replace(/\//g, '-'); // Replace slashes with dashes
      } catch (error) {
        console.error('Error formatting created date:', error);
        return '01-01-2025';
      }
    },
    
    /**
     * Get file type and size formatted as "TYPE SIZE"
     */
    getFileTypeAndSize(attachment) {
      const fileType = this.getFieldValue(attachment, 'ftype') || 'File';
      const fileSize = this.getFieldValue(attachment, 'size') || '0 B';
      return `${fileType} ${fileSize}`;
    },
    
    /**
     * Get document ID (either id or accessionId)
     */
    getDocumentId(attachment) {
      return this.getFieldValue(attachment, 'id') || this.getFieldValue(attachment, 'accessionId') || '';
    },
    
    /**
     * Check if attachment is a returned form
     */
    isReturnedForm(attachment) {
      return attachment?.title === 'doc_card_returned_form';
    },
    
    /**
     * Get doc_url from returned form attachment
     */
    getDocUrl(attachment) {
      return this.getFieldValue(attachment, 'doc_url') || '';
    },
    
    formatTimestamp(date) {
      const messageDate = new Date(date);

      // Format as MM-DD-YYYY HH:MM AM/PM to match Figma design
      const dateStr = messageDate.toLocaleDateString("en-US", {
        month: "2-digit",
        day: "2-digit",
        year: "numeric",
      }).replace(/\//g, '-'); // Replace slashes with dashes

      const timeStr = messageDate.toLocaleTimeString("en-US", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      });

      return `${dateStr}   ${timeStr}`;
    },

    /**
     * Handle preview button click - for returned forms, opens doc_url directly; otherwise generates file URL
     */
    async previewDocument(attachment) {
      try {
        // Check if this is a returned form with doc_url
        if (this.isReturnedForm(attachment)) {
          const docUrl = this.getDocUrl(attachment);
          if (docUrl) {
            console.log('🔍 Opening returned form URL in new tab:', docUrl);
            window.open(docUrl, '_blank');
            return;
          } else {
            console.error('❌ Returned form missing doc_url');
            notify({ type: 'error', message: 'Document URL not available' });
            return;
          }
        }
        
        // For uploaded files and reports, generate file URL as before
        const fileUrl = await this.getOrGenerateFileUrl(attachment);
        if (fileUrl) {
          console.log('🔍 Opening file preview in new tab:', fileUrl);
          window.open(fileUrl, '_blank');
        } else {
          console.error('❌ Failed to get file URL for preview');
        }
      } catch (error) {
        console.error('❌ Error previewing document:', error);
      }
    },

    /**
     * Handle download button click - generates file URL if needed and triggers download
     */
    async downloadDocument(attachment) {
      try {
        const fileUrl = await this.getOrGenerateFileUrl(attachment);
        if (fileUrl) {
          console.log('💾 Downloading file:', fileUrl);
          
          // Create temporary anchor element for download
          const link = document.createElement('a');
          link.href = fileUrl;
          link.download = this.getDocumentName(attachment); // Use dynamic document name
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          
          console.log('✅ File download initiated');
        } else {
          console.error('❌ Failed to get file URL for download');
        }
      } catch (error) {
        console.error('❌ Error downloading document:', error);
      }
    },

    /**
     * Get existing file URL or generate new one if needed
     * @param {Object} attachment - The attachment object
     * @returns {Promise<string|null>} - Returns file URL or null if failed
     */
    async getOrGenerateFileUrl(attachment) {
      // Check if URL already exists
      if (this.cardFileUrl) {
        console.log('✅ Using cached file URL:', this.cardFileUrl);
        return this.cardFileUrl;
      }
      
      // Generate new URL based on document type
      console.log('🔄 No cached URL found, generating new file URL...');
      this.isDownloading = true;
      
      try {
        let fileUrl;
        
        // Determine document type and use appropriate download method
        const documentId = this.getDocumentId(attachment);
        const attachmentTitle = attachment?.title;
        
        if (attachmentTitle === 'doc_card_uploaded' && documentId) {
          // Uploaded document - use file ID
          console.log('📄 Downloading uploaded file with ID:', documentId);
          fileUrl = await this.downloadUploadedFile(documentId);
        } else if (attachmentTitle === 'doc_card_report' && documentId) {
          // Report document - use accession ID
          console.log('📈 Downloading report with accession ID:', documentId);
          fileUrl = await this.downloadReportFile(documentId);
        } else {
          console.error('❌ Invalid document type or missing ID');
          return null;
        }
        
        if (fileUrl) {
          this.cardFileUrl = fileUrl; // Cache the generated URL
          console.log('✅ File URL generated and cached:', fileUrl);
          return fileUrl;
        } else {
          console.error('❌ Failed to generate file URL');
          return null;
        }
      } catch (error) {
        console.error('❌ Error generating file URL:', error);
        return null;
      } finally {
        this.isDownloading = false;
      }
    },

    /**
     * Download uploaded file from server and return blob URL
     * @param {string} fileId - The ID of the uploaded file
     * @returns {Promise<string|null>} - Returns blob URL or null if failed
     */
    async downloadUploadedFile(fileId) {
      try {
        console.log(`📥 Downloading uploaded file with ID: ${fileId}`);
        
        const response = await downloadPatientFileApi(fileId);
        
        console.log('📄 Response received:', {
          status: response.status,
          contentType: response.headers['content-type'],
          dataType: typeof response.data,
          dataSize: response.data.size,
          isBlob: response.data instanceof Blob
        });
        
        // Ensure we have a proper blob
        if (!(response.data instanceof Blob)) {
          console.error('❌ Response data is not a blob:', response.data);
          return null;
        }
        
        // Create blob URL for preview/download
        const blobUrl = URL.createObjectURL(response.data);
        
        console.log(`✅ Uploaded file downloaded successfully, blob URL created: ${blobUrl}`);
        console.log('🔍 Blob URL validation:', {
          startsWithBlob: blobUrl.startsWith('blob:'),
          fullUrl: blobUrl
        });
        
        return blobUrl;
        
      } catch (error) {
        console.error(`❌ Error downloading uploaded file with ID "${fileId}":`, error);
        if (error.response) {
          console.error('Response status:', error.response.status);
          console.error('Response data:', error.response.data);
        }
        return null;
      }
    },

    /**
     * Download report file and return blob URL
     * @param {string} accessionId - The accession ID of the report
     * @returns {Promise<string|null>} - Returns blob URL or null if failed
     */
    async downloadReportFile(accessionId) {
      try {
        console.log(`📥 Downloading report with accession ID: ${accessionId}`);
        
        const reportUrl = `https://www.vibrant-america.com/lisapi/v1/lis/base-report-service/pdf-cache/download/${accessionId}?style=classic&mode=download&timezone=Asia/Tokyo`;
        const token = sessionStorage.getItem('token');
        
        const response = await axios.get(reportUrl, {
          responseType: 'blob',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        // Create blob URL for preview/download
        const blobUrl = URL.createObjectURL(response.data);
        
        console.log(`✅ Report downloaded successfully, blob URL created`);
        return blobUrl;
        
      } catch (error) {
        console.error(`❌ Error downloading report with accession ID "${accessionId}":`, error);
        return null;
      }
    },


  },
};
</script>

<style scoped>
/* Task Card Specific Styles */
.task-card-bubble {
  background-color: #f9fbfd;
  border-radius: 20px 20px 20px 20px;
  border-radius: 20px;
  padding: 24px;
  min-width: 350px;
}

.task-card-content {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.task-header {
  display: flex;
  gap: 8px;
  align-items: center;
  margin-bottom: 8px;
}

.task-icon {
  flex-shrink: 0;
}

.task-info {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.task-title {
  font-family: "Inter", sans-serif;
  font-weight: 500;
  font-size: 16px;
  line-height: 20px;
  color: #374957;
  letter-spacing: 0.1px;
}

.task-meta {
  display: flex;
  gap: 8px;
  align-items: center;
  width: 100%;
  font-family: "Inter", sans-serif;
  font-weight: 400;
  font-size: 12px;
  line-height: 16px;
  letter-spacing: 0.4px;
}

.task-due-date {
  color: #979797;
}

.task-status {
  color: #979797;
}

.task-description {
  font-family: "Inter", sans-serif;
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  color: #333333;
  margin: 8px 0;
  letter-spacing: 0.25px;
}

.task-actions {
  display: flex;
  justify-content: flex-end;
  margin-top: 4px;
  border-top: 1px dashed var(--DividerGray, #C4C4C4);
  padding-top: 12px;
  gap: 16px;
}

.send-again-btn {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 4px;
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  font-family: "Lato", sans-serif;
  font-weight: 500;
  font-size: 14px;
  color: #1e4670;
  text-decoration: underline;
  text-underline-position: from-font;
}

.send-again-btn:hover {
  opacity: 0.8;
}

.send-again-btn span {
  white-space: nowrap;
}
.message-container {
  display: flex;
  gap: 12px;
  margin-bottom: 16px;
  max-width: 100%;
}

/* User messages (right side) */
.user-message {
  flex-direction: row-reverse;
}

/* Not user messages (left side) */
.not-user-message {
  flex-direction: row;
}

.avatar-img {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  object-fit: cover;
  flex-shrink: 0;
}

.message-content {
  flex: 1;
  min-width: 0;
  max-width: 100%;
}

.user-content {
  display: flex;
  flex-direction: column;
  margin-left: 118px;
  align-items: flex-end;
}

.not-user-content {
  display: flex;
  flex-direction: column;
  margin-right: 118px;
  align-items: flex-start;
}

.message-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 4px;
}

.user-header {
  flex-direction: row-reverse;
}

.not-user-header {
  flex-direction: row;
}

.sender-name {
  font-family: "IBM Plex Sans Hebrew", sans-serif;
  font-weight: 500;
  font-size: 14px;
  line-height: 20px;
  color: #333333;
}

.channel-tag {
  background-color: #e6f3ff;
  color: #004879;
  padding: 2px 8px;
  border-radius: 12px;
  font-family: "IBM Plex Sans Hebrew", sans-serif;
  font-weight: 400;
  font-size: 12px;
  line-height: 16px;
}

.message-bubble {
  position: relative;
  width: 100%;
  word-wrap: break-word;
  max-width: 600px;
}

.bubble-content {
  padding: 12px 24px;
  font-family: "Inter", sans-serif;
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  letter-spacing: 0.25px;
  position: relative;
  border: 1px solid var(--DividerGray, #C4C4C4);
  color: #000000;
}

/* User message bubble styling - matches Figma design */

/* Not user message bubble styling */
.not-user-bubble {
  background-color: #f9fbfd;
  color: #333333;
  border-radius: 20px 20px 20px 20px;
}

.message-timestamp {
  margin-top: 4px;
  font-family: "IBM Plex Sans Hebrew", sans-serif;
  font-weight: 500;
  font-size: 11px;
  line-height: 16px;
  color: #999999;
  letter-spacing: 0.5px;
}

.user-timestamp {
  text-align: right;
}

.not-user-timestamp {
  text-align: left;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .message-content {
    max-width: calc(100vw - 80px);
  }

  .bubble-content {
    font-size: 14px;
    line-height: 20px;
    padding: 10px 14px;
  }
}

/* Bot/System message styles */
.bot-centered-message {
  flex-direction: row;
}

.bot-user-content {
  display: flex;
  flex-direction: column;
  margin-left: 118px;
  margin-right: 118px;
  align-items: center;
}

.bot-user-header {
  color: var(--ContentMediumGrey, #999);
  text-align: center;
  font-family: "IBM Plex Sans Hebrew", sans-serif;
  font-size: 11px;
  font-style: normal;
  font-weight: 500;
  line-height: 16px;
  letter-spacing: 0.5px;
}
</style>
